import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  Trophy, Target, Zap, TrendingUp, Gift, Star, Crown, Flame,
  CheckCircle, Clock, Lock, Award, ChevronRight, Play, Volume2
} from 'lucide-react';

const GameDashboard = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [player, setPlayer] = useState(null);
  const [season, setSeason] = useState(null);
  const [missions, setMissions] = useState([]);
  const [powerUps, setPowerUps] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [activePowerUps, setActivePowerUps] = useState([]);
  const [advisorTips, setAdvisorTips] = useState([]);
  const [selectedTab, setSelectedTab] = useState('overview');
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (user) {
      initializeGame();
    }
  }, [user]);

  const initializeGame = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Process daily login
      await fetch(`${backendUrl}/api/member/game/login`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      // Load all game data
      await Promise.all([
        loadPlayer(),
        loadSeason(),
        loadMissions(),
        loadPowerUps(),
        loadLeaderboard(),
        loadActivePowerUps(),
        loadAdvisorTips()
      ]);
      
      setLoading(false);
    } catch (error) {
      console.error('Error initializing game:', error);
      setLoading(false);
    }
  };

  const loadPlayer = async () => {
    const token = localStorage.getItem('token');
    const response = await fetch(`${backendUrl}/api/member/game/profile`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    const data = await response.json();
    if (data.ok) setPlayer(data.player);
  };

  const loadSeason = async () => {
    const token = localStorage.getItem('token');
    const response = await fetch(`${backendUrl}/api/member/game/season`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    const data = await response.json();
    if (data.ok) setSeason(data.season);
  };

  const loadMissions = async () => {
    const token = localStorage.getItem('token');
    const response = await fetch(`${backendUrl}/api/member/game/missions`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    const data = await response.json();
    if (data.ok) setMissions(data.missions);
  };

  const loadPowerUps = async () => {
    const token = localStorage.getItem('token');
    const response = await fetch(`${backendUrl}/api/member/game/power-ups`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    const data = await response.json();
    if (data.ok) setPowerUps(data.power_ups);
  };

  const loadLeaderboard = async () => {
    const token = localStorage.getItem('token');
    const response = await fetch(`${backendUrl}/api/member/game/leaderboard?limit=10`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    const data = await response.json();
    if (data.ok) setLeaderboard(data.leaderboard);
  };

  const loadActivePowerUps = async () => {
    const token = localStorage.getItem('token');
    const response = await fetch(`${backendUrl}/api/member/game/power-ups/active`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    const data = await response.json();
    if (data.ok) setActivePowerUps(data.active_power_ups);
  };

  const loadAdvisorTips = async () => {
    const token = localStorage.getItem('token');
    const response = await fetch(`${backendUrl}/api/member/game/advisor/tips`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    const data = await response.json();
    if (data.ok) setAdvisorTips(data.tips);
  };

  const purchasePowerUp = async (powerUpCode) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/member/game/power-ups/${powerUpCode}/purchase`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      
      if (data.ok) {
        alert(data.message);
        await Promise.all([loadPlayer(), loadActivePowerUps()]);
      } else {
        alert('Failed to purchase power-up');
      }
    } catch (error) {
      console.error('Error purchasing power-up:', error);
      alert('Failed to purchase power-up');
    }
  };

  const claimMissionRewards = async (missionCode) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/member/game/missions/${missionCode}/claim`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      
      if (data.ok) {
        alert(`🎉 Rewards claimed! +${data.points_reward} points, +${data.xp_reward} XP, +${data.credits_reward} credits`);
        await Promise.all([loadPlayer(), loadMissions()]);
      }
    } catch (error) {
      console.error('Error claiming rewards:', error);
    }
  };

  const dismissAdvisorTip = async (tipCode) => {
    const token = localStorage.getItem('token');
    await fetch(`${backendUrl}/api/member/game/advisor/dismiss/${tipCode}`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    setAdvisorTips(advisorTips.filter(tip => tip.code !== tipCode));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Trophy className="w-16 h-16 text-blue-600 mx-auto mb-4 animate-pulse" />
          <p className="text-gray-600">Loading Game...</p>
        </div>
      </div>
    );
  }

  if (!player) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Trophy className="w-16 h-16 text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Failed to load game data</p>
        </div>
      </div>
    );
  }

  const daysRemaining = season ? Math.ceil((new Date(season.end_date) - new Date()) / (1000 * 60 * 60 * 24)) : 0;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Advisor Tips Banner */}
      {advisorTips.length > 0 && (
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg">
          <div className="max-w-7xl mx-auto px-4 py-3">
            {advisorTips.map((tip) => (
              <div key={tip.code} className="flex items-start gap-3">
                <div className="flex-shrink-0 mt-1">
                  <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                    <span className="text-2xl">{tip.icon}</span>
                  </div>
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-lg">{tip.title}</h4>
                  <p className="text-blue-100 text-sm mt-1">{tip.message}</p>
                  {tip.voiceover_url && (
                    <button className="mt-2 flex items-center gap-2 text-sm bg-white/20 px-3 py-1 rounded-full hover:bg-white/30">
                      <Volume2 className="w-4 h-4" />
                      Play Audio Guide
                    </button>
                  )}
                </div>
                <button
                  onClick={() => dismissAdvisorTip(tip.code)}
                  className="text-white/80 hover:text-white"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold mb-2">🎮 Game Dashboard</h1>
              <p className="text-blue-100">
                {player.business_name || user.full_name} • Level {player.current_level} • {player.business_tier.replace('_', ' ')}
              </p>
            </div>
            <div className="text-right">
              {player.login_streak >= 3 && (
                <div className="flex items-center gap-2 text-2xl">
                  <Flame className="w-8 h-8 text-orange-400" />
                  <span className="font-bold">{player.login_streak} Day Streak!</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-2">
              <Trophy className="w-8 h-8 text-yellow-500" />
              <span className="text-2xl font-bold text-gray-900">{player.season_points}</span>
            </div>
            <p className="text-gray-600 text-sm">Season Points</p>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-2">
              <Target className="w-8 h-8 text-blue-500" />
              <span className="text-2xl font-bold text-gray-900">{player.total_leads_captured}</span>
            </div>
            <p className="text-gray-600 text-sm">Total Leads</p>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-2">
              <Zap className="w-8 h-8 text-purple-500" />
              <span className="text-2xl font-bold text-gray-900">{player.available_credits}</span>
            </div>
            <p className="text-gray-600 text-sm">Credits</p>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-2">
              <Star className="w-8 h-8 text-green-500" />
              <span className="text-2xl font-bold text-gray-900">{player.pif_conversions}</span>
            </div>
            <p className="text-gray-600 text-sm">PIF Conversions</p>
          </div>
        </div>

        {/* Season Progress */}
        {season && (
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-xl font-bold text-gray-900">{season.name}</h2>
                <p className="text-gray-600">{season.description}</p>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-blue-600">{daysRemaining}</div>
                <p className="text-gray-600 text-sm">Days Remaining</p>
              </div>
            </div>
            <div className="bg-gray-200 rounded-full h-3">
              <div
                className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-full h-3"
                style={{ width: `${((30 - daysRemaining) / 30) * 100}%` }}
              />
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-4 mb-6 border-b border-gray-200">
          {['overview', 'missions', 'power-ups', 'leaderboard'].map((tab) => (
            <button
              key={tab}
              onClick={() => setSelectedTab(tab)}
              className={`px-6 py-3 font-semibold capitalize ${
                selectedTab === tab
                  ? 'border-b-2 border-blue-600 text-blue-600'
                  : 'text-gray-600 hover:text-blue-600'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {selectedTab === 'overview' && (
          <div className="space-y-6">
            {/* Active Power-Ups */}
            {activePowerUps.length > 0 && (
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                  <Zap className="w-5 h-5 text-yellow-500" />
                  Active Power-Ups
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {activePowerUps.map((pu) => (
                    <div key={pu.id} className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-lg p-4 border-2 border-yellow-400">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-semibold">{pu.power_up_code.replace('_', ' ')}</p>
                          {pu.multiplier && <p className="text-sm text-gray-600">{pu.multiplier}x Multiplier</p>}
                        </div>
                        <div className="text-right text-sm">
                          <p className="text-gray-600">Expires:</p>
                          <p className="font-semibold">{new Date(pu.expires_at).toLocaleTimeString()}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Quick Missions */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-lg font-bold mb-4">Today's Quick Wins</h3>
              <div className="space-y-3">
                {missions.filter(m => m.mission.mission_type === 'daily').slice(0, 3).map((mission) => (
                  <div key={mission.mission.code} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{mission.mission.icon}</span>
                      <div>
                        <p className="font-semibold">{mission.mission.name}</p>
                        <p className="text-sm text-gray-600">{mission.progress}/{mission.required}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      {mission.status === 'completed' && !mission.rewards_claimed ? (
                        <button
                          onClick={() => claimMissionRewards(mission.mission.code)}
                          className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center gap-2"
                        >
                          <Gift className="w-4 h-4" />
                          Claim
                        </button>
                      ) : (
                        <span className="text-sm text-gray-600">+{mission.mission.points_reward} pts</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {selectedTab === 'missions' && (
          <div className="space-y-6">
            {['daily', 'weekly', 'seasonal'].map((type) => (
              <div key={type} className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-lg font-bold mb-4 capitalize">{type} Missions</h3>
                <div className="space-y-3">
                  {missions.filter(m => m.mission.mission_type === type).map((mission) => (
                    <div key={mission.mission.code} className="border rounded-lg p-4 hover:bg-gray-50">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-2xl">{mission.mission.icon}</span>
                            <h4 className="font-semibold">{mission.mission.name}</h4>
                            {mission.status === 'completed' && (
                              <CheckCircle className="w-5 h-5 text-green-600" />
                            )}
                          </div>
                          <p className="text-sm text-gray-600 mb-2">{mission.mission.description}</p>
                          {mission.mission.tip && (
                            <p className="text-xs text-blue-600 bg-blue-50 p-2 rounded">
                              💡 {mission.mission.tip}
                            </p>
                          )}
                          <div className="mt-3">
                            <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
                              <span>Progress: {mission.progress}/{mission.required}</span>
                            </div>
                            <div className="bg-gray-200 rounded-full h-2">
                              <div
                                className="bg-blue-600 rounded-full h-2"
                                style={{ width: `${(mission.progress / mission.required) * 100}%` }}
                              />
                            </div>
                          </div>
                        </div>
                        <div className="text-right ml-4">
                          <div className="text-sm text-gray-600 mb-2">Rewards:</div>
                          {mission.mission.points_reward > 0 && (
                            <div className="text-sm">+{mission.mission.points_reward} pts</div>
                          )}
                          {mission.mission.xp_reward > 0 && (
                            <div className="text-sm">+{mission.mission.xp_reward} XP</div>
                          )}
                          {mission.mission.credits_reward > 0 && (
                            <div className="text-sm">+{mission.mission.credits_reward} credits</div>
                          )}
                          {mission.status === 'completed' && !mission.rewards_claimed && (
                            <button
                              onClick={() => claimMissionRewards(mission.mission.code)}
                              className="mt-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center gap-2"
                            >
                              <Gift className="w-4 h-4" />
                              Claim
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {selectedTab === 'power-ups' && (
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-lg font-bold mb-4">Power-Up Shop</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {powerUps.map((pu) => (
                <div
                  key={pu.code}
                  className={`border-2 rounded-xl p-6 ${
                    pu.can_purchase ? 'border-blue-300 hover:shadow-lg' : 'border-gray-200 opacity-60'
                  }`}
                >
                  <div className="text-center mb-4">
                    <div className="text-4xl mb-2">{pu.icon}</div>
                    <h4 className="font-bold text-lg">{pu.name}</h4>
                    <p className="text-sm text-gray-600 mt-2">{pu.description}</p>
                  </div>
                  <div className="text-center mb-4">
                    <div className="text-2xl font-bold text-blue-600">{pu.credit_cost}</div>
                    <div className="text-sm text-gray-600">Credits</div>
                    {pu.duration_minutes && (
                      <div className="text-xs text-gray-500 mt-1">
                        Duration: {Math.floor(pu.duration_minutes / 60)}h {pu.duration_minutes % 60}m
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => purchasePowerUp(pu.code)}
                    disabled={!pu.can_purchase}
                    className={`w-full py-2 rounded-lg font-semibold ${
                      pu.can_purchase
                        ? 'bg-blue-600 text-white hover:bg-blue-700'
                        : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    {pu.can_purchase ? 'Purchase' : pu.credits_needed > 0 ? `Need ${pu.credits_needed} more` : 'Locked'}
                  </button>
                  {pu.tier_required !== 'rookie_boot_camp' && (
                    <div className="text-xs text-center text-gray-500 mt-2">
                      <Lock className="w-3 h-3 inline mr-1" />
                      Requires: {pu.tier_required.replace('_', ' ')}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {selectedTab === 'leaderboard' && (
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
              <Crown className="w-6 h-6 text-yellow-500" />
              Season Leaderboard
            </h3>
            <div className="space-y-3">
              {leaderboard.map((entry, index) => (
                <div
                  key={entry.user_id}
                  className={`flex items-center gap-4 p-4 rounded-lg ${
                    entry.user_id === user.id ? 'bg-blue-50 border-2 border-blue-300' : 'bg-gray-50'
                  }`}
                >
                  <div className="text-2xl font-bold text-gray-400 w-8 text-center">
                    {index === 0 && '🥇'}
                    {index === 1 && '🥈'}
                    {index === 2 && '🥉'}
                    {index > 2 && `#${entry.rank}`}
                  </div>
                  <div className="w-10 h-10 bg-gray-300 rounded-full flex items-center justify-center text-xl">
                    {entry.avatar_url ? (
                      <img src={entry.avatar_url} alt="" className="w-full h-full rounded-full" />
                    ) : (
                      '👤'
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold">{entry.business_name}</div>
                    <div className="text-sm text-gray-600">
                      {entry.leads_captured} leads • {entry.pif_conversions} PIFs
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-xl font-bold text-blue-600">{entry.points}</div>
                    <div className="text-xs text-gray-600">points</div>
                  </div>
                  {entry.is_on_fire && (
                    <Flame className="w-6 h-6 text-orange-500" />
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GameDashboard;
